import Promise from 'bluebird';
import _ from 'underscore';
import $ from 'jquery';

import { levelAtConstant } from '@bingads-webui-campaign/scope-constants';
import { stringifySync } from '@bingads-webui/safe-json';
import { Constants as CCUIConstants } from '@bingads-webui-clientcenter/entity-utils';
import * as downloadService from '@bingads-webui/download-service';

import {
  getFilteredEntitiesAndUpdateOptions,
  handleUnauthorizedResponse,
} from '@bingads-webui-campaign/auction-insight-service';
import { withDFMCache } from '@bingads-webui/dfm-cache';

import {
  RECOMMENDATION_TYPES,
  SUPPORTED_CAMPAIGN_TYPES,
  TASK_STATUS,
  APPLY_ODATA_TYPES_MAP,
  FAC_RECOMMENDATION_CONFIG,
  FAC_RECOMENDATIONTYPE_FEATUREID_MAPPING,
  CHANNEL_TYPES,
  APPLY_ACTION_TYPES,
  NOT_SUPPORT_DOWNLOAD_TYPES,
  RECOMMENDATION_CATEGORIES,
  SUPPORT_ADINSIGHT_AUTO_APPLY_TYPES,
  INLIE_APPLY_ERROR_SUPPORT_TYPES,
  MCC_ACCOUNTS_CAPPING,
} from '../consts';

import {
  getRecommendationIds,
  getRecommendationTypes,
  isCompetition,
  isDFMEnabled,
} from '../util';

import {
  PollTask,
} from '../poll';

import {
  formatPercentOneDecimal,
} from '../formatter/basic-format-data';

import {
  setRecommendationCache,
  getRecommendationCache,
} from '../cache';

let overallOptimizationScoreBar = {};
let applyNotificationInfo = { message: '', failedRecommendationCount: 0, userAction: '' };
const urlPrefix = 'AdInsight/V2/Customers({cid})/Accounts({aid})';
const urlPrefixMCC = 'AdInsight/V2/Customers({cid})';
const getSummaryAPI = `${urlPrefix}/Recommendation.GetRecSummaryData?$expand=Recommendations({top}{skip}{orderby}$expand=Opportunities)`;
const getSummaryTypeOnly = `${urlPrefix}/Recommendation.GetRecSummaryData`;
const getSummaryAPIOptimizationScoreNoExpand = `${urlPrefix}/Recommendation.GetRecSummaryDataWithOptimizationScore`;
const getSummaryAPIOptimizationScore = `${urlPrefix}/Recommendation.GetRecSummaryDataWithOptimizationScore?$expand=Summaries($expand=Recommendations({top}{skip}{orderby}$expand=Opportunities))`;
const getSummaryAPIOptimizationScoreWithDismissedTab = `${urlPrefix}/Recommendation.GetRecSummaryDataWithOptimizationScore?$expand=Summaries($expand=Recommendations({top}{skip}{orderby}$expand=Opportunities)),DismissTabSummaries($expand=Recommendations({top}{skip}{orderby}$expand=Opportunities))`;
const getSummaryAPIMCC = `${urlPrefixMCC}/Recommendation.GetSummaryData?$expand=Recommendations({top}{skip}{orderby}$expand=Opportunities)`;
const getHistoryAPI = `${urlPrefix}/Recommendation.GetApplyHistory?{topQuery}&{skipQuery}&$count=true&$orderby=Timestamp+desc&$filter=(TotalRecommendationCount+gt+0+or+IsAARSuperUser+eq+false)`;
const getHistoryAPIMCC = `${urlPrefixMCC}/Recommendation.GetApplyHistory?{topQuery}&{skipQuery}&$count=true&$orderby=Timestamp+desc&$filter=(TotalRecommendationCount+gt+0+or+IsAARSuperUser+eq+false)`;
const getAutoApplyOptInStatusAPI = `${urlPrefix}/Recommendation.GetRecommendationAutoApplyOptInStatus`;
const setAutoApplyOptInStatusAPI = `${urlPrefix}/Recommendation.SetRecommendationAutoApplyOptInStatus`;
const getCountAPI = `${urlPrefix}/Opportunity.GetSummaryData?$select=RecommendationsCount`;
const getStockImageAPI = '/Customers({cid})/Accounts({aid})/StockImages';
const getBulkAarUploadBlobAPI = `${urlPrefixMCC}/BulkOperationTools.GetUploadUrlForBulkEditAAROptInStatus`;
const submitBulkAarOptInStatusTaskAPI = `${urlPrefixMCC}/BulkOperationTools.SubmitBulkEditAAROptInStatusTask`;
const pollBulkEditAAROptInStatusTaskAPI = `${urlPrefixMCC}/BulkOperationTools.PollBulkEditAAROptInStatusTask`;
const fetchDownloadIdAPI = '/Recommendation.SubmitReportDataTask';
const applyAPISuffix = 'Apply';
const applyAllAPISuffix = 'ApplyAll';
const applyRecBulkAPISuffix = 'ApplyRecBulk';
const applyRecAllAPISuffix = 'ApplyRecAll';
const redismissAPI = 'Redismiss';
const trackAPISuffix = 'Track';
const FAC_STATUS_NOT_IN_PROGRESS = CCUIConstants.RecommendationAdoptionStatus.NotInProgess;
const FAC_STATUS_ACTIVE = CCUIConstants.RecommendationAdoptionStatus.Active;

const getOptions = (token, lcid, data, type = 'POST') => ({
  contentType: 'application/json',
  type,
  headers: {
    authorization: `CCMTSmallToken ${token}`,
    'x-ms-lcid': `${lcid}`,
  },
  data: stringifySync(data),
});

const formatUrl = (url, scope, top, skip, orderby, accountId) => url
  .replace('{aid}', accountId || scope.accountId)
  .replace('{cid}', scope.customerId)
  .replace(/{top}/g, _.isNumber(top) ? `$top=${top};` : '')
  .replace(/{skip}/g, _.isNumber(skip) ? `$skip=${skip};` : '')
  .replace('{topQuery}', _.isNumber(top) ? `$top=${top}` : '')
  .replace('{skipQuery}', _.isNumber(skip) ? `$skip=${skip}` : '')
  .replace(/{orderby}/g, !_.isEmpty(orderby) ? `$orderby=${_.map(orderby, i => i.join(' ')).join(',')};` : '');

const getRequestScope = (op, scope, activity) => {
  let campaignAdGroups = [];
  const { LevelAt, EntityIds } = getFilteredEntitiesAndUpdateOptions(op, scope);

  if (LevelAt === levelAtConstant.CAMPAIGN) {
    campaignAdGroups = _.map(EntityIds, id => ({ Key: id, Value: [] }));
  } else if (LevelAt === levelAtConstant.ADGROUP) {
    if (scope.levelAt !== levelAtConstant.ACCOUNT) {
      campaignAdGroups = [{
        Key: parseInt(scope.campaignId, 10),
        Value: EntityIds,
      }];
    } else {
      // in account scope with ad group filter
      activity.trace(
        'fall back to return all recomemndations in account scope with ad group filter',
        'RecommendationService/getRequestScope'
      );
    }
  }

  return campaignAdGroups;
};

function addCouponToRecommendations(rawCouponData, rawRecommendationData) {
  if (rawCouponData.length) {
    const coupon = rawCouponData[0];
    const couponFeatureId = coupon.FeatureId.toString();
    if (_.isUndefined(FAC_RECOMMENDATION_CONFIG[couponFeatureId])) {
      return;
    }
    const priorities = FAC_RECOMMENDATION_CONFIG[couponFeatureId].priority;

    const couponEligibleRec = _.chain(rawRecommendationData)
      .filter(rec => _.contains(priorities, rec.OpportunityType))
      .sortBy(rec => _.indexOf(priorities, rec.OpportunityType))
      .first()
      .value();

    if (couponEligibleRec) {
      _.extend(couponEligibleRec, { Coupon: coupon });
    }
  }
}

export class RecommendationService {
  constructor({
    scope,
    showAlertHandler,
    i18n,
    odataPath,
    currentScenario,
    currentActivity,
    permissions,
    lcid,
    pageContext,
    ccJsData,
    asyncScheduler,
    isMCC = false,
    accountIdsPromise,
    alertCenter = {},
    odata = null,
    isNoExpand = false,
    newI18n,
    cacheManager = null,
    channel,
  }) {
    this.scope = scope;
    this.i18n = i18n;
    this.showAlertHandler = showAlertHandler;
    this.cache = {};
    this.currentScenario = currentScenario;
    this.currentActivity = currentActivity;
    this.odataPath = odataPath;
    this.permissions = permissions;
    this.lcid = lcid;
    this.isMCC = isMCC;
    this.isNoExpand = isNoExpand;
    this.accountIdsPromise = accountIdsPromise || Promise.resolve([]);
    this.hasAccountIdsPromise = !!accountIdsPromise;
    this.ccJsData = ccJsData;
    this.asyncScheduler = asyncScheduler;
    this.pollStatusTask = null;
    this.alertCenter = alertCenter;
    this.odata = odata;
    this.accountInfoCache = {};
    this.pageContext = pageContext;
    this.cappedAccountIdsPromise =
      this.accountIdsPromise.then(accountIds => accountIds.slice(0, MCC_ACCOUNTS_CAPPING));
    this.isOptimizationScoreOn = !isMCC && (channel === CHANNEL_TYPES.RECOMMENDATION
      || channel === CHANNEL_TYPES.OVERVIEW
      || channel === CHANNEL_TYPES.INCONTEXTCARD
      || channel === CHANNEL_TYPES.CAMPAIGN_SUMMARY_BAR);
    this.isDismissedTabEnabled = this.isOptimizationScoreOn && _.result(permissions, 'IsDismissedTabEnabled');
    this.newI18n = newI18n;
    this.cacheManager = cacheManager;
  }

  startCouponAdoption = (adoption) => {
    if (this.ccJsData
      && adoption) {
      return Promise.resolve(this.ccJsData.FeatureAdoptionCoupon
        .update(
          adoption.id,
          {
            adoption: {
              ...adoption,
              Status: FAC_STATUS_ACTIVE,
            },
          }
        ))
        .tapCatch((err) => {
          if (this.showAlertHandler) {
            const errorCode = err.Errors && err.Errors[0] && err.Errors[0].Code;
            const errorMessageKey = errorCode === 'CouponBankDepleted'
              ? 'FACDepletedErrorMessage'
              : 'GenericFACAdoptionStartErrorMessage';

            this.showAlertHandler({
              message: this.i18n.getString(errorMessageKey),
              level: 'Error',
              dismissible: true,
            });
          }
        });
    }
    return Promise.resolve();
  };

  getSummaryPromiseFromOption = (op, includeCoupons, campaignAdGroups) => (option) => {
    if (!option) {
      return Promise.resolve({ data: [] });
    }

    const isWithDFM = (op.channel === CHANNEL_TYPES.RECOMMENDATION
        || (op.channel === CHANNEL_TYPES.MCCRECOMMENDATION && !op.type))
      && !op.isFromApplyOrDismiss
      && isDFMEnabled();

    let fetchDataPromise;
    if (isWithDFM) {
      fetchDataPromise = () => new Promise((resolve, reject) => {
        $.ajax(option).then((rawData) => {
          resolve(rawData);
        }, reject);
      });
      fetchDataPromise = withDFMCache(fetchDataPromise, 'getSummaryDFM', this);
    } else {
      fetchDataPromise = new Promise((resolve, reject) => {
        $.ajax(option).then((rawData) => {
          resolve(rawData);
        }, reject);
      });
    }

    let getCouponPromise;
    if (op.channel !== CHANNEL_TYPES.RECOMMENDATION) {
      // don't take reject param, if coupon call fails, we will just not show the offer
      getCouponPromise = new Promise((resolve) => {
        if (includeCoupons
          && !this.isMCC
          && this.ccJsData) {
          const getCouponsActivity = this.currentScenario.getCoupons.create();

          getCouponsActivity.start();
          // if call fails, eat the error to avoid blocking workflow
          resolve(this.ccJsData.FeatureAdoptionCoupon
            .findAll({ aid: this.scope.accountId })
            .then((coupons) => {
              getCouponsActivity.stop();
              return _.filter(
                coupons,
                ({ Status }) =>
                  Status === FAC_STATUS_NOT_IN_PROGRESS || Status === FAC_STATUS_ACTIVE
              );
            })
            .catch((err) => {
              if (err instanceof TypeError) {
                getCouponsActivity.trace(`TypeError for JsData.FeatureAdoptionCoupon, most likely due to making the request after being logged out: ${err.message}`);
              } else if (err === undefined) {
                getCouponsActivity.trace('err was undefined for getCoupons JsData response');
              } else if (err.name === undefined || err.message === undefined) {
                getCouponsActivity.trace(`likely a timeout or 400/500/unusable response from FeatureAdoptionCoupon server API,
                                          err name: ${err.name}, err.message: ${err.message}`);
              } else {
                getCouponsActivity.error(
                  `error in getCoupons: ${err.name}: ${err.message}`,
                  'RecommendationService/getCoupons'
                );
              }
              getCouponsActivity.stop();
              return [];
            }));
        } else {
          resolve([]);
        }
      });
    }
    const getSummaryActivity = this.currentScenario.getSummary.create();

    getSummaryActivity.start();

    return (isWithDFM ? fetchDataPromise() : fetchDataPromise).then((rawData) => {
      getSummaryActivity.stop();
      return rawData;
    }).then((rawData) => {
      if (this.isOptimizationScoreOn) {
        let overallScoreIncrease = 0;
        const {
          message,
          failedRecommendationCount,
          allExpiredInSummary,
          userAction,
          totalRecommendationCount,
        } = applyNotificationInfo;

        // if op.type is not undefined means from detail page, and we only update current score
        // under 2 situations, #1 is from summay page, #2 is when user directly go to detail page
        if (!_.isEmpty(rawData.OverallOptimizationScoreBar)
          && (!op.type || (_.isEmpty(overallOptimizationScoreBar) && op.type))) {
          overallScoreIncrease =
            Number(rawData.OverallOptimizationScoreBar.Gained +
              rawData.OverallOptimizationScoreBar.Dismissed) -
            Number(overallOptimizationScoreBar.Gained +
              overallOptimizationScoreBar.Dismissed);
          overallOptimizationScoreBar = rawData.OverallOptimizationScoreBar;
        }

        if (op.isFromApplyOrDismiss
          && totalRecommendationCount > 0
          && (userAction !== APPLY_ACTION_TYPES.ACCEPT
            || failedRecommendationCount === 0
            || allExpiredInSummary
          )) {
          const level = failedRecommendationCount > 0 && !allExpiredInSummary
            ? 'Warning'
            : 'Info';
          this.showAlertHandler({
            message: !_.isString(message) ? message : `${message}${overallScoreIncrease > 0 && op.categoryType !== RECOMMENDATION_CATEGORIES.DISMISSED ?
              ` +${formatPercentOneDecimal(overallScoreIncrease, this.i18n)}` : ''}`,
            level,
            dismissible: true,
          });
        }
      }
      return rawData;
    })
      .then((rawData) => {
        if (rawData && rawData.Summaries) {
          return _.extend(rawData, { value: rawData.Summaries });
        }
        return rawData;
      })
      .then((rawData) => {
        // if loaded recommendations don't map to coupons,
        // don't wait for coupon promise to resolve
        if (includeCoupons
          && op.channel !== CHANNEL_TYPES.RECOMMENDATION
          && _.any(
            rawData && rawData.value,
            rec => _.has(FAC_RECOMENDATIONTYPE_FEATUREID_MAPPING, rec.OpportunityType)
          )) {
          return getCouponPromise.then((rawCouponData) => {
            addCouponToRecommendations(rawCouponData, rawData.value);

            return {
              data: rawData,
              campaignAdGroups,
            };
          });
        }

        return {
          data: rawData,
          campaignAdGroups,
        };
      })
      .then((rawData) => {
        // Cache recommendation summary data
        const isRecommendationSummaryPage =
          (op.channel === CHANNEL_TYPES.RECOMMENDATION
          || op.channel === CHANNEL_TYPES.MCCRECOMMENDATION)
          && _.isNull(op.type);
        if (this.permissions.IsRecommendationCacheDetailPageDataEnabled
          && isRecommendationSummaryPage) {
          setRecommendationCache('recommendation-summary-raw-data', rawData);
        }
        return rawData;
      })
      .catch((err) => {
        if (this.showAlertHandler) {
          this.handleAjaxError(err, option, 'getSummary', op.shouldShowAlertHandler);
        } else {
          throw (err);
        }
        // Return an object same as "no recommendation"
        // if ajax error occurs for recommendation page
        return op.channel === CHANNEL_TYPES.COMPETITION ? {} : {
          data: {
            CategoryScoreUpLifts: [],
            Id: 0,
            OverallOptimizationScoreBar: null,
            Summaries: [],
            value: [],
          },
          campaignAdGroups,
        };
      });
  };

  getCoupon = (op) => {
    // don't take reject param, if coupon call fails, we will just not show the offer
    const getCouponPromise = new Promise((resolve) => {
      if (!op.ignoreCoupons
        && !this.isMCC
        && this.ccJsData) {
        const getCouponsActivity = this.currentScenario.getCoupons.create();

        getCouponsActivity.start();
        // if call fails, eat the error to avoid blocking workflow
        resolve(this.ccJsData.FeatureAdoptionCoupon
          .findAll({ aid: this.scope.accountId })
          .then((coupons) => {
            getCouponsActivity.stop();
            return _.filter(
              coupons,
              ({ Status }) =>
                Status === FAC_STATUS_NOT_IN_PROGRESS || Status === FAC_STATUS_ACTIVE
            );
          })
          .catch((err) => {
            if (err instanceof TypeError) {
              getCouponsActivity.trace(`TypeError for JsData.FeatureAdoptionCoupon, most likely due to making the request after being logged out: ${err.message}`);
            } else if (err === undefined) {
              getCouponsActivity.trace('err was undefined for getCoupons JsData response');
            } else if (err.name === undefined || err.message === undefined) {
              getCouponsActivity.trace(`likely a timeout or 400/500/unusable response from FeatureAdoptionCoupon server API,
                                        err name: ${err.name}, err.message: ${err.message}`);
            } else {
              getCouponsActivity.error(
                `error in getCoupons: ${err.name}: ${err.message}`,
                'RecommendationService/getCoupons'
              );
            }
            getCouponsActivity.stop();
            return [];
          }));
      } else {
        resolve([]);
      }
    });

    return getCouponPromise;
  };

  getSummary = (op) => {
    this.currentActivity.trace('getSummary is called', 'RecommendationService/getSummary');

    const includeCoupons = !op.ignoreCoupons;
    const campaignAdGroups = op.campaignAdGroups ||
      getRequestScope(op, op.scope || this.scope, this.currentActivity);

    const optionPromise = this.generateGetSummaryRequest(_.extend({}, op, { campaignAdGroups }));
    if (!optionPromise) {
      return Promise.resolve({ data: [] });
    }
    if (this.hasAccountIdsPromise) {
      return optionPromise
        .then(this.getSummaryPromiseFromOption(op, includeCoupons, campaignAdGroups));
    }

    return this.getSummaryPromiseFromOption(op, includeCoupons, campaignAdGroups)(optionPromise);
  };

  getSummaryRequestFromAccountIds = op => (accountIds) => {
    const {
      type,
      channel,
      campaignType,
      top,
      skip,
      orderby,
      filterOpportunity,
      campaignAdGroups,
      detailPageSetting,
      accountId,
    } = op;
    let data;

    let paginationQueryOptions;
    if ((channel === CHANNEL_TYPES.RECOMMENDATION
      || channel === CHANNEL_TYPES.MCCRECOMMENDATION)
      && !_.isNull(type)
      && type === RECOMMENDATION_TYPES.IMAGE_EXTENSION) {
      paginationQueryOptions = JSON.stringify({
        $orderby: !_.isEmpty(orderby) ? _.map(orderby, i => i.join(' ')).join(',') : undefined,
        $top: _.isNumber(top) ? top.toString() : undefined,
        $skip: _.isNumber(skip) ? skip.toString() : undefined,
      });
    }

    const getOptimizationScore =
      (channel === CHANNEL_TYPES.RECOMMENDATION
      || channel === CHANNEL_TYPES.INCONTEXTCARD
      || channel === CHANNEL_TYPES.CAMPAIGN_SUMMARY_BAR
      || channel === CHANNEL_TYPES.OVERVIEW)
      && this.isOptimizationScoreOn;
    const OpportunityTypes = getRecommendationIds(
      type,
      channel,
      this.permissions,
      this.isMCC
    );
    if (this.isMCC && _.isUndefined(accountId)) {
      if (_.isEmpty(accountIds)) {
        return null;
      }

      data = {
        Request: {
          Channel: channel,
          RecommendationTypes: OpportunityTypes,
          AccountIds: accountIds,
          DetailPageSetting: detailPageSetting,
          RawFilterQueryExpression: filterOpportunity,
          PaginationQueryOptions: paginationQueryOptions,
        },
      };
    } else {
      data = {
        Request: {
          CampaignAdGroups: campaignAdGroups,
          CampaignTypeId:
            _.contains(SUPPORTED_CAMPAIGN_TYPES, campaignType) ?
              campaignType : null,
          Channel: channel,
          RecommendationTypes: OpportunityTypes,
          RawFilterQueryExpression: filterOpportunity,
          PaginationQueryOptions: paginationQueryOptions,
        },
      };
    }

    if (_.has(op, 'settingOptions')) {
      _.extend(data.Request, { SettingOptions: op.settingOptions });
    }

    const option = getOptions(this.scope.token, this.lcid, data);

    let api = getSummaryAPI;
    if (this.isMCC && _.isUndefined(accountId)) {
      api = getSummaryAPIMCC;
    } else if (getOptimizationScore) {
      if (this.isNoExpand) {
        api = getSummaryAPIOptimizationScoreNoExpand;
      } else {
        api = this.isDismissedTabEnabled ?
          getSummaryAPIOptimizationScoreWithDismissedTab : getSummaryAPIOptimizationScore;
      }
    } else if (this.isNoExpand) {
      api = getSummaryTypeOnly;
    }

    option.url = formatUrl(
      `${this.odataPath}${api}`,
      this.scope,
      top,
      skip,
      orderby,
      accountId
    );
    return option;
  };

  generateGetSummaryRequest = (op) => {
    const {
      type,
      channel,
    } = op;
    const OpportunityTypes = getRecommendationIds(
      type,
      channel,
      this.permissions,
      this.isMCC
    );

    if (!channel || !OpportunityTypes || OpportunityTypes.length === 0) {
      return null;
    }
    if (!this.hasAccountIdsPromise) {
      return this.getSummaryRequestFromAccountIds(op)([]);
    }
    return this.cappedAccountIdsPromise
      .then(this.getSummaryRequestFromAccountIds(op)).catch((err) => {
        if (this.showAlertHandler) {
          this.handleAjaxError(err, {}, 'getAccountIds');
        } else {
          throw (err);
        }
        return null;
      });
  };

  getHistory = (op) => {
    this.currentActivity.trace('getHistory is called', 'RecommendationService/getHistory');
    const option = this.generateGetHistoryRequest(_.extend({}, op));
    const fetchDataPromise = new Promise((resolve, reject) => {
      $.ajax(option).then((rawData) => {
        resolve(rawData);
      }, reject);
    });


    const getHistoryActivity = this.currentScenario.getHistory.create();
    getHistoryActivity.start();

    return fetchDataPromise.then(
      (rawData) => {
        getHistoryActivity.stop();
        return {
          data: rawData,
        };
      },
      (err) => {
        if (this.showAlertHandler) {
          this.handleAjaxError(err, option, 'getHistory');
        } else {
          throw (err);
        }
        return {};
      }
    );
  };

  generateGetHistoryRequest = ({ top, skip }) => {
    const option = getOptions(this.scope.token, this.lcid, undefined, 'GET');

    option.url = formatUrl(`${this.odataPath}${this.isMCC ? getHistoryAPIMCC : getHistoryAPI}`, this.scope, top, skip);
    return option;
  };

  // getCount returns count of recommendations in account level only.
  getCount = (op) => {
    this.currentActivity.trace('getCount is called', 'RecommendationService/getCount');

    const option = this.generateGetCountRequest(op);
    if (!option) {
      return Promise.resolve({ data: [] });
    }
    const fetchDataPromise = new Promise((resolve, reject) => {
      $.ajax(option).then((rawData) => {
        resolve(rawData);
      }, reject);
    });

    const getCountActivity = this.currentScenario.getCount.create();
    getCountActivity.start();

    return fetchDataPromise.then(
      (rawData) => {
        getCountActivity.stop();
        return {
          data: rawData,
        };
      },
      (err) => {
        if (this.showAlertHandler) {
          this.handleAjaxError(err, option, 'getCount');
        } else {
          throw (err);
        }
        return {};
      }
    );
  };

  generateGetCountRequest = (op) => {
    if (_.isUndefined(op)) {
      return null;
    }

    const {
      channel,
      campaignType,
    } = op;
    const OpportunityTypes = getRecommendationIds(
      null,
      channel,
      this.permissions,
      this.isMCC
    );

    if (!channel || _.isEmpty(OpportunityTypes)) {
      return null;
    }

    const data = {
      Request: {
        Channel: channel,
        OpportunityTypes,
        CampaignTypeId:
        _.contains(SUPPORTED_CAMPAIGN_TYPES, campaignType) ?
          campaignType : null,
      },
    };
    const option = getOptions(this.scope.token, this.lcid, data);

    option.url = formatUrl(`${this.odataPath}${getCountAPI}`, this.scope);
    return option;
  };

  // getCountByCampaignAdGroups returns count of recommendations by given campaign and ad groups
  getCountByCampaignAdGroups = (op) => {
    this.currentActivity.trace('getCountByCampaignAdGroups is called', 'RecommendationService/getCountByCampaignAdGroups');

    const campaignAdGroups = op.campaignAdGroups ||
      getRequestScope(op, op.scope || this.scope, this.currentActivity);
    const option = this.generateGetCountByCampaignAdGroupsRequest(_.extend(
      {}, op,
      { campaignAdGroups }
    ));
    if (!option) {
      return Promise.resolve({ data: [] });
    }
    const fetchDataPromise = new Promise((resolve, reject) => {
      $.ajax(option).then((rawData) => {
        resolve(rawData);
      }, reject);
    });

    const getCountByCampaignAdGroupsActivity =
      this.currentScenario.getCountByCampaignAdGroups.create();
    getCountByCampaignAdGroupsActivity.start();

    return fetchDataPromise.then(
      (rawData) => {
        getCountByCampaignAdGroupsActivity.stop();
        return {
          data: rawData,
        };
      },
      (err) => {
        this.handleAjaxError(err, option, 'getCountByCampaignAdGroups');
        return {};
      }
    );
  };

  generateGetCountByCampaignAdGroupsRequest = (op) => {
    const {
      channel,
      campaignAdGroups,
      type,
    } = op;

    const OpportunityTypes = getRecommendationIds(
      type,
      channel,
      this.permissions,
      this.isMCC
    );

    if (!channel || !OpportunityTypes || OpportunityTypes.length === 0) {
      return null;
    }

    const data = {
      Request: {
        CampaignAdGroups: campaignAdGroups,
        Channel: channel,
        OpportunityTypes,
      },
    };
    const option = getOptions(this.scope.token, this.lcid, data);

    option.url = formatUrl(`${this.odataPath}${getCountAPI}`, this.scope);
    return option;
  };

  logRecommendationFailedResults = (rawData, type) => {
    _.chain(rawData.AggregatedResults)
      .filter(res => res.ErrorCode !== 0)
      .each(res => _.each(res.OpportunityIds, (opportunityId) => {
        this.currentActivity.trace(
          `apply recommendation failed, opportunityId = ${opportunityId}, errorCode = ${res.ErrorCode}, type = ${type}`, // eslint-disable-line
          'recommendation/applyFailed'
        );
      }));
  };

  countError = (rawData) => {
    const totalCount = _.reduce(
      rawData.AggregatedResults,
      (memo, singleCodeResult) => memo + singleCodeResult.RecommendationCount,
      0
    );
    const successfulCount = _.chain(rawData.AggregatedResults)
      .filter(singleCodeResult => singleCodeResult.ErrorCode === 0)
      .first()
      .result('RecommendationCount')
      .value() || 0;
    const failedCount = totalCount - successfulCount;
    const expiredCount = _.chain(rawData.AggregatedResults)
      .map(singleCodeResult => singleCodeResult.ExpiredCount)
      .reduce((memo, count) => memo + count, 0)
      .value();
    _.extend(rawData, {
      totalCount,
      successfulCount,
      failedCount,
      expiredCount,
    });
  };

  getErrors = rawData => _.uniq(_.map(
    rawData.AggregatedResults,
    item => _.pick(item, 'ErrorCode', 'ErrorMessage', 'Property')
  ), false, item => item.ErrorCode);

  dealWithInlineError = (rawData, hasInlineError) => {
    if (_.isFunction(hasInlineError)) {
      const errors = this.getErrors(rawData);
      if (hasInlineError(errors)) {
        throw errors;
      }
    }
  };

  aggregateResult = (rawData) => {
    if (_.isUndefined(rawData.AggregatedResults)) {
      _.extend(rawData, {
        AggregatedResults: _.chain(rawData.Results)
          .groupBy('ErrorCode')
          .map((items, key) => ({
            ErrorCode: parseInt(key, 10),
            ErrorMessage: _.first(items).ErrorMessage,
            OpportunityIds: _.map(items, item => item.OpportunityId),
            RecommendationCount: _.size(items),
            ExpiredCount: _.size(_.filter(items, item => item.Expired)),
            Property: _.first(items).Property,
          }))
          .value(),
      });
    }
  };

  dealApplyResponse = ({ rawData, op }) => {
    this.aggregateResult(rawData);
    this.countError(rawData);
    const { totalCount: total, failedCount: fail } = rawData;
    if (fail && isCompetition(op.type)) {
      const localizedkey = `Recommendation_${op.userAction}_Error_Message`;

      this.showAlertHandler({
        message: this.i18n.getString(localizedkey, { total, fail }),
        level: 'Info',
        dismissible: true,
      });
    }

    if (!isCompetition(op.type)) {
      if (rawData.totalCount === 0) {
        if (rawData.ErrorCode && rawData.ErrorCode !== 0) {
          // If ErrorCode is not equal to 0, the error should be notified.
          // Even though the totalCount, failedCount are 0.
          this.showAlertHandler({
            message: this.i18n.getString('GenericErrorMessage'),
            level: 'Error',
            dismissible: true,
          });
        }
        this.setApplyNotificationInfo({ message: '', failedRecommendationCount: 0, userAction: '' });

        return [];
      }

      this.logRecommendationFailedResults(rawData, op.type);
      if (_.contains(INLIE_APPLY_ERROR_SUPPORT_TYPES, op.type)) {
        this.dealWithInlineError(rawData, op.hasInlineError);
      }

      op.handleRecommendationNotification({
        i18n: this.i18n,
        op,
        rawData,
        showAlertHandler: this.showAlertHandler,
        isOptimizationScoreOn: this.isOptimizationScoreOn,
        setApplyNotificationInfo: this.setApplyNotificationInfo,
        newI18n: this.newI18n,
        scope: this.scope,
      });
    }

    return [];
  };

  stopStatusPoll = () => {
    if (!_.isNull(this.pollStatusTask)) {
      this.pollStatusTask.stop();
    }
  };

  track = (op) => {
    this.currentActivity.trace(`track is called, UserAction= ${stringifySync(op.userAction)}`, 'RecommendationService/track');

    // Apply/dismiss recommendation: invalidate recommendation summary data cache.
    // Call it early to prevent the timeout on aync apply.
    if (this.permissions.IsRecommendationCacheDetailPageDataEnabled
      && !_.isUndefined(getRecommendationCache('recommendation-summary-raw-data'))) {
      setRecommendationCache('recommendation-summary-raw-data', undefined);
    }

    let isAsync = false;

    return this.generateApplyRequest(op).then((option) => {
      const result = new Promise((resolve, reject) => {
        $.ajax(option).then((rawData) => {
          resolve(rawData);
        }, reject);
      });

      const getApplyActivity = this.currentScenario.track.create();
      getApplyActivity.start();

      return result.then((rawData = {}) => {
        getApplyActivity.stop();
        if (rawData.IsAsync) {
          isAsync = true;
          this.pollStatusTask = new PollTask(
            () => $.ajax(this.generateApplyStatusRequest({
              adoptionId: rawData.AdoptionId,
            })),
            data => data.AdoptionStatus === TASK_STATUS.COMPLETED,
            data => data.AdoptionStatus === TASK_STATUS.FAILED,
            this.asyncScheduler,
            10000 // max poll count
          );
          if (_.isFunction(op.closeUiBlock)) {
            op.closeUiBlock();
          }
          if (_.isFunction(op.showAsyncApplyModal)) {
            op.showAsyncApplyModal(this.pollStatusTask);
          }

          return this.pollStatusTask.start().then((data) => {
            this.currentActivity.trace(`poll count: ${this.pollStatusTask.pollCount}`, 'RecommendationService/Track');
            return data;
          });
        }
        return rawData;
      })
        .then(
          (rawData = {}) => op.userAction !== APPLY_ACTION_TYPES.REDISMISS &&
            this.dealApplyResponse({ rawData, op }),
          (err) => {
            this.handleAjaxError(err, option, 'track');
            return [];
          }
        );
    }).finally(() => {
      if (isAsync) {
        if (_.isFunction(op.closeAsyncApplyModal)) {
          op.closeAsyncApplyModal();
        }
      } else if (_.isFunction(op.closeUiBlock)) {
        op.closeUiBlock();
      }

      if (this.permissions.IsRecommendationDataCacheEnabled
        && !_.isNull(this.cacheManager)) {
        // Apply/dismiss all on in context card recommendation: invalidate campaign cache.
        // Apply/dismiss on recommendation page: invalidate it when leaving recommendation page.
        if (op.channel === CHANNEL_TYPES.INCONTEXTCARD) {
          this.cacheManager.invalidate('Campaign');
        } else if (op.channel === CHANNEL_TYPES.RECOMMENDATION
          || op.channel === CHANNEL_TYPES.MCCRECOMMENDATION) {
          setRecommendationCache('need-invalidate-campaign-cache', true);
        }
      }
    });
  };

  getApplyAPI = (withAll, type, asyncEnabled = false, userAction) => {
    let suffix = asyncEnabled ? applyRecBulkAPISuffix : applyAPISuffix;

    if (withAll) {
      suffix = asyncEnabled ? applyRecAllAPISuffix : applyAllAPISuffix;
    } else if (
      (type === RECOMMENDATION_TYPES.SITE_LINK ||
        type === RECOMMENDATION_TYPES.CALLOUT) &&
      !asyncEnabled
    ) {
      suffix = trackAPISuffix; // todo: discuss with MT
    }

    if (userAction === APPLY_ACTION_TYPES.REDISMISS) {
      suffix = redismissAPI;
    }

    return `${this.isMCC ? urlPrefixMCC : urlPrefix}/${asyncEnabled ? 'Recommendation' : 'Opportunity'}.${suffix}`;
  };

  isTrackingOnly = (type, asyncEnabled) => (type === RECOMMENDATION_TYPES.SITE_LINK ||
    type === RECOMMENDATION_TYPES.CALLOUT) &&
    asyncEnabled;

  getOdataType = (withAll, type, asyncEnabled) => {
    const isAll = withAll ? 'ALL' : 'BULK';
    if (this.isMCC) {
      return APPLY_ODATA_TYPES_MAP[isAll].MCC;
    } else if (asyncEnabled) {
      return APPLY_ODATA_TYPES_MAP[isAll].SINGLE_ACCOUNT_ASYNC;
    }

    return APPLY_ODATA_TYPES_MAP[isAll].SINGLE_ACCOUNT;
  };

  generateApplyRequest = (op) => {
    const {
      type,
      channel,
      userAction,
      withAll,
      userInputs,
      campaignAdGroups,
      settings,
      reason,
      context,
      guid,
      totalRecommendationCount,
    } = op;

    return this.cappedAccountIdsPromise.then((accountIds) => {
      const asyncEnabled = this.isMCC ||
        (
          this.permissions.IsBAARecommendationsAsyncApplyEnabled &&
          (channel === CHANNEL_TYPES.RECOMMENDATION ||
            channel === CHANNEL_TYPES.INCONTEXT ||
            channel === CHANNEL_TYPES.INCONTEXTCARD)
        ) ||
        (
          this.isOptimizationScoreOn &&
          (channel === CHANNEL_TYPES.RECOMMENDATION ||
            channel === CHANNEL_TYPES.INCONTEXT ||
            channel === CHANNEL_TYPES.INCONTEXTCARD)
        );

      const isFromDismissTab = _.result(context, 'Category', '') === RECOMMENDATION_CATEGORIES.DISMISSED;

      const data = {
        Request: {
          '@odata.type': this.getOdataType(withAll, type, asyncEnabled),
          [`${asyncEnabled ? 'RecommendationType' : 'OpportunityType'}`]: type,
          AccountIds: this.isMCC ? accountIds : undefined,
          UserAction: userAction,
          UserInputs: userInputs || [],
          CampaignAdGroups: this.isMCC ? undefined : campaignAdGroups,
          Channel: channel,
          Settings: settings,
          Reason: reason,
          Context: JSON.stringify(context),
          Guid: guid,
          TotalRecommendationCount: asyncEnabled && withAll ? totalRecommendationCount : undefined,
          TrackingOnly: asyncEnabled ? this.isTrackingOnly(type, asyncEnabled) : undefined,
        },
      };

      if (isFromDismissTab) {
        data.Request.IsFromDismissTab = true;
      }

      const option = getOptions(this.scope.token, undefined, data);

      option.url = formatUrl(`${this.odataPath}${this.getApplyAPI(withAll, type, asyncEnabled, userAction)}`, this.scope);
      return option;
    }).catch((err) => {
      if (this.showAlertHandler) {
        this.handleAjaxError(err, {}, 'getAccountIds');
      } else {
        throw (err);
      }
      return null;
    });
  };

  generateApplyStatusRequest = (op) => {
    const {
      adoptionId,
    } = op;
    const option = getOptions(this.scope.token, undefined, undefined, 'GET');

    option.url = formatUrl(
      `${this.odataPath}${this.isMCC ? urlPrefixMCC : urlPrefix}/Recommendation.GetApplyStatus(Id='${adoptionId}')`,
      this.scope
    );
    return option;
  };

  handleAjaxError = (err, option, functionName, shouldShowAlertHandler = true) => {
    const errorDetails = `ErrorMessage=${stringifySync(err.message)}, ErrorStack=${stringifySync(err.stack)}, ErrorCode=${err.status}, Error=${stringifySync(err)}, Request=${stringifySync(option.data)}`;

    if (err.status === 0) {
      this.currentActivity.trace(`Request not able to be sent, ${errorDetails}`, `RecommendationService/${functionName}`);
    } else if (err.status === 401) {
      this.currentActivity.trace(`error in ${functionName}, ${errorDetails}`, `RecommendationService/${functionName}`);
      handleUnauthorizedResponse(err);
    } else {
      this.currentActivity.error(`error in ${functionName}, ${errorDetails}`, `RecommendationService/${functionName}`);
    }

    if (shouldShowAlertHandler) {
      this.showAlertHandler({
        message: this.i18n.getString('GenericErrorMessage'),
        level: 'Error',
        dismissible: true,
      });
    }

    // When it's failed to apply, set applyNotificationInfo to default value.
    this.setApplyNotificationInfo({ message: '', failedRecommendationCount: 0, userAction: '' });
  };

  /**
   * Creates a download task in MT by giving MT the adoption Id
   * @param {string} adoptionId - the adoption's id to download its history
   * @returns {Promise} Resolves to download task id if the download was successful.
   */
  fetchDownloadIdHistory = (adoptionId) => {
    const data = {
      Request: {
        '@odata.type': '#Microsoft.Advertiser.AdInsight.Opportunity.ODataApiModel.ApplyErrorDetailsDownloadTaskRequest',
        ReportType: 'ApplyErrorDetailsDownload',
        AdoptionId: adoptionId,
      },
    };
    const option = getOptions(this.scope.token, undefined, data);

    option.url = formatUrl(
      `${this.odataPath}${this.isMCC ? urlPrefixMCC : urlPrefix}${fetchDownloadIdAPI}`,
      this.scope
    );
    return new Promise((resolve, reject) => {
      $.ajax(option).then((rawData) => {
        resolve(rawData);
      }, reject);
    });
  };

  fetchDownloadIdRecommendation = ({
    recommendationType,
    channel,
    campaignType,
  }) => this.accountIdsPromise.then((accountIds) => {
    const recommendationTypes = _.difference(
      getRecommendationTypes(
        recommendationType,
        channel,
        this.permissions,
        this.isMCC
      ),
      NOT_SUPPORT_DOWNLOAD_TYPES
    );

    let uniqParams = {};

    if (this.isMCC) {
      uniqParams = {
        '@odata.type': '#Microsoft.Advertiser.AdInsight.Opportunity.ODataApiModel.MccRecommendationsDownloadTaskRequest',
        AccountIds: accountIds,
      };
    } else {
      let campaignScope = null;
      let adgroupScope = null;

      campaignScope = this.scope && this.scope.campaignId
        ? { CampaignId: parseInt(this.scope.campaignId, 10) }
        : null;
      adgroupScope = this.scope && this.scope.adGroupId ?
        {
          AdGroupId: parseInt(this.scope.adGroupId, 10),
          CampaignId: parseInt(this.scope.campaignId, 10),
        } : null;

      const scopes = {};

      if (adgroupScope) {
        scopes.AdGroupScopes = [adgroupScope];
      } else if (campaignScope) {
        scopes.CampaignScopes = [campaignScope];
      }

      uniqParams = {
        '@odata.type': '#Microsoft.Advertiser.AdInsight.Opportunity.ODataApiModel.RecommendationsDownloadTaskRequest',
        CampaignTypeId: _.contains(SUPPORTED_CAMPAIGN_TYPES, campaignType) ? campaignType : null,
        ...scopes,
      };
    }

    const data = {
      Request: {
        ReportType: 'RecommendationsDownload',
        Channel: channel,
        RecommendationTypes: recommendationTypes,
        ...uniqParams,
      },
    };

    const option = getOptions(this.scope.token, undefined, data);

    option.url = formatUrl(
      `${this.odataPath}${this.isMCC ? urlPrefixMCC : urlPrefix}${fetchDownloadIdAPI}`,
      this.scope
    );

    return new Promise((resolve, reject) => {
      $.ajax(option).then((rawData) => {
        resolve(rawData);
      }, reject);
    });
  });

  /**
   *
   * @param {string} id - download task id
   * @returns {Promise} Resolves to be the url of download file
   */
  pollFileName = id => new PollTask(
    () => $.ajax(this.generatePollFileNameRequest(id)),
    data => data.Status === TASK_STATUS.COMPLETED,
    data => data.Status === TASK_STATUS.FAILED,
    this.asyncScheduler
  );

  generatePollFileNameRequest = (id) => {
    const option = getOptions(this.scope.token, undefined, undefined, 'GET');

    option.url = formatUrl(
      `${this.odataPath}${this.isMCC ? urlPrefixMCC : urlPrefix}/Recommendation.PollReportDataTask(Id='${id}')`,
      this.scope
    );
    return option;
  };

  /**
   * poll server to know if file is ready and then download it
   * @param {string} downloadId - the id to fetch download file status
   * @returns {Promise} Resolves to poll Count if the download was successful.
   */
  downloadWithDownloadId = ({ Id: downloadId }) => {
    let alertId = null;
    this.currentActivity.trace(`Download task started with id: ${downloadId}`, 'RecommendationService/DownloadWithDownloadId');
    // show an alert saying that download request was successfully submitted
    alertId = this.alertCenter.showConfirmation(
      this.i18n.getString('GridDownloadStatusMessage'),
      alertId
    );
    const pollTask = this.pollFileName(downloadId);
    return pollTask.start()
      .finally(() => this.alertCenter.dismiss(alertId))
      .then(({ ResultFile }) => {
        downloadService.iframeDownload(ResultFile);
        return pollTask.pollCount;
      });
  };

  /**
   * download of recommendations
   * Creates a download request and POSTS to odata, asynchronously polls for the status of the
   * request and initiates the browser download after the file has been generated.
   * @param {array} recommendationType -
   * the recommendation type to be download. Null stands for all.
   * @param {string} channel - different channel has different supported recommendation types
   * @param {string} campaignType - campaign type selected in new UI
   * @returns {Promise} Resolves to true if the download was successful.
   */
  download = ({
    recommendationType,
    channel,
    campaignType,
  }) => this.fetchDownloadIdRecommendation({
    recommendationType,
    channel,
    campaignType,
  }).then(this.downloadWithDownloadId)
    .then((pollCount) => {
      this.currentActivity.trace(`poll count: ${pollCount}`, 'RecommendationService/Download');
    })
    .catch(err => this.handleAjaxError(err, 'download'));

  /**
   * Creates a download request and POSTS to odata, asynchronously polls for the status of the
   * request and initiates the browser download after the file has been generated.
   * @param {string} adoptionId - the adoption's id to download its history
   * @returns {Promise} Resolves to true if the download was successful.
   */
  downloadHistory = (adoptionId) => {
    this.currentActivity.trace(`Download history task started with adoption id: ${adoptionId}`, 'RecommendationService/DownloadHistory');
    return this.fetchDownloadIdHistory(adoptionId)
      .then(this.downloadWithDownloadId)
      .then((pollCount) => {
        this.currentActivity.trace(`poll count: ${pollCount}`, 'RecommendationService/DownloadHistory');
      })
      .catch(err => this.handleAjaxError(err, 'downloadHistory'));
  };

  setApplyNotificationInfo = (options) => {
    applyNotificationInfo = options;
  };

  getAutoApplyOptInStatus = () => {
    this.currentActivity.trace('getAutoApplyOptInStatus is called', 'RecommendationService/getAutoApplyOptInStatus');
    const option = this.generateGetAutoApplyOptInStatusRequest();
    const fetchDataPromise = new Promise((resolve, reject) => {
      $.ajax(option).then((rawData) => {
        resolve(rawData);
      }, reject);
    });


    const getAutoApplyOptInStatusActivity = this.currentScenario.getAutoApplyOptInStatus.create();
    getAutoApplyOptInStatusActivity.start();

    return fetchDataPromise.then(
      (rawData) => {
        getAutoApplyOptInStatusActivity.stop();
        return {
          data: rawData,
        };
      },
      (err) => {
        if (this.showAlertHandler) {
          this.handleAjaxError(err, option, 'getAutoApplyOptInStatus');
        } else {
          throw (err);
        }
        return {};
      }
    );
  };

  generateGetAutoApplyOptInStatusRequest = () => {
    const option = getOptions(
      this.scope.token, undefined,
      {
        Request: {
          RecommendationTypes: SUPPORT_ADINSIGHT_AUTO_APPLY_TYPES,
        },
      }
    );
    option.url = formatUrl(`${this.odataPath}${getAutoApplyOptInStatusAPI}`, this.scope);
    return option;
  };

  setAutoApplyOptInStatus = (op) => {
    this.currentActivity.trace(`setAutoApplyOptInStatus is called, isOptInStatusDelta= ${stringifySync(op.isOptInStatusDelta)}`, 'RecommendationService/setAutoApplyOptInStatus');

    if (_.isEmpty(op.isOptInStatusDelta)) {
      this.showAlertHandler({
        message: this.newI18n.getString(_TL_('Auto-apply preference updated.')),
        level: 'Info',
        dismissible: true,
      });

      return Promise.resolve();
    }

    const option = this.generateSetAutoApplyOptInStatusRequest(op);
    const result = new Promise((resolve, reject) => {
      $.ajax(option).then((rawData) => {
        resolve(rawData);
      }, reject);
    });

    const setAutoApplyOptInStatusActivity =
      this.currentScenario.setAutoApplyOptInStatus.create();
    setAutoApplyOptInStatusActivity.start();

    return result.then((rawData = {}) => {
      setAutoApplyOptInStatusActivity.stop();
      return rawData;
    })
      .then(
        (rawData = {}) => {
          const isSetAutoApplyOptInStatusSuccess = rawData.ErrorCode === 0;
          this.showAlertHandler({
            message: this.newI18n.getString(_TL_(isSetAutoApplyOptInStatusSuccess ?
              'Auto-apply preference updated.' : 'Auto-apply preference failed to update. Please try again later.')),
            level: isSetAutoApplyOptInStatusSuccess ? 'Info' : 'Warning',
            dismissible: true,
          });
        },
        (err) => {
          this.handleAjaxError(err, option, 'setAutoApplyOptInStatus');
          return [];
        }
      );
  };

  generateSetAutoApplyOptInStatusRequest = (op) => {
    const AAROptInStatusList = [];
    _.each(op.isOptInStatusDelta, (status, type) => {
      AAROptInStatusList.push({ Key: type, Value: status });
    });

    const option = getOptions(this.scope.token, undefined, { Request: { AAROptInStatusList } });
    option.url = formatUrl(`${this.odataPath}${setAutoApplyOptInStatusAPI}`, this.scope);
    return option;
  };

  getStockImageUrl = (queryString) => {
    const culture = _.get(window, ['globalLegacyI18n', 'context', 'culture']);
    const url = formatUrl(`${getStockImageAPI}`, this.scope);

    const query = _.extend({
      $top: 1,
      $skip: 0,
      appName: 'AdInsight',
      $search: queryString,
      language: culture,
      includevertical: false,
    });

    return this.odata.get(url, _.extend({ data: query }, this.odata));
  };

  getBulkAarUploadBlobUrl = (op) => {
    this.currentActivity.trace(`getBulkAarUploadBlobUrl is called, fileName= ${stringifySync(op.fileName)}`, 'RecommendationService/getBulkAarUploadBlobUrl');

    const option = this.generateBulkAarUploadBlobUrlRequest(op);
    const result = new Promise((resolve, reject) => {
      $.ajax(option).then((rawData) => {
        resolve(rawData);
      }, reject);
    });

    const getBulkAarUploadBlobUrlActivity =
      this.currentScenario.getBulkAarUploadBlobUrl.create();
    getBulkAarUploadBlobUrlActivity.start();

    return result.then((rawData = {}) => {
      getBulkAarUploadBlobUrlActivity.stop();
      return rawData;
    })
      .then(
        rawData => ({ data: rawData }),
        (err) => {
          this.handleAjaxError(err, option, 'getBulkAarUploadBlobUrl');
          return [];
        }
      );
  };

  generateBulkAarUploadBlobUrlRequest = (op) => {
    const option = getOptions(this.scope.token, this.lcid, { Request: { UploadedFileName: op.fileName } });

    option.url = formatUrl(`${this.odataPath}${getBulkAarUploadBlobAPI}`, this.scope);
    return option;
  };

  setBulkAarUploadBlob = (url, data) => {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('x-ms-blob-type', 'BlockBlob');

    return fetch(url, {
      headers,
      method: 'PUT',
      body: data,
    }).then(
      response => response,
      () => {
        this.showAlertHandler({
          message: this.newI18n.getString(_TL_('Upload failed. Please try again later.')),
          level: 'Error',
          dismissible: true,
        });
      }
    );
  };

  submitBulkAarOptInStatusTask = (op) => {
    this.currentActivity.trace(`submitBulkAarOptInStatusTask is called, fileName= ${stringifySync(op.fileName)}`, 'RecommendationService/submitBulkAarOptInStatusTask');

    const option = this.generateSubmitBulkAarOptInStatusTaskRequest(op);
    const result = new Promise((resolve, reject) => {
      $.ajax(option).then((rawData) => {
        resolve(rawData);
      }, reject);
    });

    const submitBulkAarOptInStatusTaskActivity =
      this.currentScenario.submitBulkAarOptInStatusTask.create();
    submitBulkAarOptInStatusTaskActivity.start();

    return result.then((rawData = {}) => {
      submitBulkAarOptInStatusTaskActivity.stop();
      return rawData;
    })
      .then(
        rawData => ({ res: rawData }),
        (err) => {
          if (err.status === 400) {
            this.showAlertHandler({
              message: err.responseJSON.error.message,
              level: 'Error',
              dismissible: true,
            });
          } else if (err.status === 401) {
            this.showAlertHandler({
              message: this.newI18n.getString(_TL_('You don\'t have access to make updates for the account id.')),
              level: 'Error',
              dismissible: true,
            });
          } else {
            this.handleAjaxError(err, option, 'submitBulkAarOptInStatusTask');
          }
          return [];
        }
      );
  };

  generateSubmitBulkAarOptInStatusTaskRequest = (op) => {
    const option = getOptions(this.scope.token, this.lcid, { Request: { UploadedFileName: op.fileName } });

    option.url = formatUrl(`${this.odataPath}${submitBulkAarOptInStatusTaskAPI}`, this.scope);
    return option;
  };

  pollBulkAarOptInStatusTask = (op) => {
    this.currentActivity.trace(`pollBulkAarOptInStatusTask is called, reqId= ${stringifySync(op.reqID)}`, 'RecommendationService/pollBulkAarOptInStatusTask');

    this.pollStatusTask = new PollTask(
      () => $.ajax(this.generatepollBulkAarOptInStatusTaskRequest(op)),
      data => data.BulkUpdateStatus === TASK_STATUS.COMPLETED,
      data => data.BulkUpdateStatus === TASK_STATUS.FAILED,
      this.asyncScheduler,
      10000 // max poll count
    );

    return this.pollStatusTask.start().then((data) => {
      this.currentActivity.trace(`poll count: ${this.pollStatusTask.pollCount}`, 'RecommendationService/pollBulkAarOptInStatusTask');
      return data;
    }).then(
      (rawData = {}) => this.dealBulkAarOptInStatusResponse({ rawData }),
      (err) => {
        this.handleAjaxError(err, null, 'pollBulkAarOptInStatusTask');
        return [];
      }
    );
  };

  generatepollBulkAarOptInStatusTaskRequest = (op) => {
    const option = getOptions(this.scope.token, this.lcid, {}, 'GET');

    option.url = formatUrl(`${this.odataPath}${pollBulkEditAAROptInStatusTaskAPI}(Id='${op.reqId}')`, this.scope);
    return option;
  };

  aggregateBulkAarResult = (rawData) => {
    if (_.isUndefined(rawData.AggregatedResults)) {
      _.extend(rawData, {
        AggregatedResults: _.chain(_.flatten(_.map(_.chain(rawData.Results).value(), item => _.map(item.Results, resu => ({
          Id: item.Id,
          ...resu,
        })))))
          .groupBy('ErrorCode')
          .map((items, key) => ({
            ErrorCode: parseInt(key, 10),
            Count: _.size(items),
            ...items,
          }))
          .value(),
      });
    }
  };

  countBulkError = (rawData) => {
    const totalCount = _.reduce(
      rawData.AggregatedResults,
      (memo, singleCodeResult) => memo + singleCodeResult.Count,
      0
    );
    const successfulCount = _.filter(rawData.AggregatedResults, singleCodeResult => singleCodeResult.ErrorCode === 0).length ?
      _.first(_.filter(rawData.AggregatedResults, singleCodeResult => singleCodeResult.ErrorCode === 0)).Count : 0;
    const failedCount = totalCount - successfulCount;
    _.extend(rawData, {
      totalCount,
      successfulCount,
      failedCount,
    });
  };

  dealBulkAarOptInStatusResponse = ({ rawData }) => {
    this.aggregateBulkAarResult(rawData);
    this.countBulkError(rawData);

    if (rawData.ErrorCode !== 0) {
      this.showAlertHandler({
        message: this.newI18n.getString(_TL_('Applied fail.')),
        level: 'Error',
        dismissible: true,
      });
    } else {
      this.showAlertHandler({
        message: this.newI18n.getString(_TL_('{{successCount}} applied successfully!'), { successCount: rawData.successfulCount }),
        level: 'Info',
        dismissible: true,
      });
    }

    return [];
  };
}
